<script lang="ts">
	import { clone } from "@bufbuild/protobuf";
	import { Label } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { PageSchema } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import ImageButtonAppearanceEditor from "#lib/components/flow/editor/appearance/ImageButtonAppearanceEditor.svelte";
	import StateEditor from "#lib/components/flow/editor/appearance/StateEditor.svelte";
	import { IMAGE_BUTTON_DEFAULT } from "#lib/components/flow/editor/appearance/constants.js";
	import EditImage from "#lib/components/images/EditImage.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { isDataProperty, matchAspectRatioPreset } from "#lib/components/images/utils.js";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import { getImageDimensions } from "#lib/image.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import type { JsonObject, JsonValue } from "#lib/protobuf-compat.js";
	import { numberValue, setNumberValue, setStringValue, setStructValue, stringValue, structValue, unsetValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	let { id, page, onChange }: ComponentEditingProps = $props();

	const provider: Writable<TokenProvider> = getContext("tokenProvider");

	let lastError: unknown = $state();
	let captionInitiallyUnset = $state(false);
	let subheaderInitiallyUnset = $state(false);

	const config = $derived(structValue($page.config, id));
	const caption = $derived(stringValue(config, "caption"));
	const subheader = $derived(stringValue(config, "subheader"));
	const src = $derived(stringValue(config, "src"));
	const className = $derived(stringValue(config, "class"));
	const aspectRatio = $derived(numberValue(config, "aspectRatio"));
	const globalLastError: Writable<Error | undefined> = getContext("lastError");

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	onMount(() => {
		captionInitiallyUnset = stringValue(structValue($page.config, id), "caption") === undefined;
		subheaderInitiallyUnset = stringValue(structValue($page.config, id), "subheader") === undefined;
	});

	function updateCaption(e: CustomEvent<string | undefined>) {
		const config = getConfig();
		if (!e.detail && captionInitiallyUnset) {
			unsetValue(config, "caption");
		} else {
			setStringValue(config, "caption", e.detail ?? "");
		}
		$page = clone(PageSchema, $page);
	}

	function updateSubheader(e: CustomEvent<string | undefined>) {
		const config = getConfig();
		if (!e.detail && subheaderInitiallyUnset) {
			unsetValue(config, "subheader");
		} else {
			setStringValue(config, "subheader", e.detail ?? "");
		}
		$page = clone(PageSchema, $page);
	}

	function handleChange(e: CustomEvent<string | undefined>) {
		onChange?.(e.detail);
	}

	async function updateImage(e: CustomEvent<ImagePickerChangeEvent>) {
		const config = getConfig();
		const nextValue = e.detail.value?.trim() ?? "";
		setStringValue(config, "src", nextValue);
		// Data properties aren't loadable URLs — skip dimension lookup so save still completes.
		if (nextValue && !isDataProperty(nextValue)) {
			try {
				const { width, height } = await getImageDimensions(nextValue);
				const preset = matchAspectRatioPreset(width / height);
				if (preset !== undefined) {
					setNumberValue(config, "aspectRatio", preset);
				} else {
					unsetValue(config, "aspectRatio");
				}
			} catch {
				$globalLastError = new Error("Couldn't read the image size. Saved without an aspect ratio.");
			}
		} else {
			unsetValue(config, "aspectRatio");
		}
		$page = $page;
		onChange?.({ ...e.detail, value: nextValue } as unknown as JsonValue);
	}

	export function validate(): boolean {
		return true;
	}
</script>

<EditorInset>
	<div class={twMerge("flex flex-col px-6", className)}>
		<div class="flex flex-col gap-6">
			<FeatureFlagGate flag="build_your_own_page">
				<div class="flex w-full flex-col gap-5 rounded-lg border p-3">
					<P class="text-sm font-bold text-black"><FeatureFlagGate flag="editor_toolbar_revamp_q4_2026">Content{#snippet fallback()}Attributes{/snippet}</FeatureFlagGate></P>
					<div class="flex flex-col gap-2">
						<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Card</P>
						<div class="flex gap-4 border-l-[5px] border-l-gray-300 pl-6">
							<div class="flex flex-col gap-1">
								<Label for="{id}-image-label" class="font-medium text-black">Image</Label>
								<EditImage class="h-30 w-30 rounded-lg" bind:error={lastError} value={src} on:change={updateImage} {aspectRatio} initialAspectRatio={IMAGE_BUTTON_DEFAULT.aspectRatio} showAspectRatioPicker={true} preferFreeformAspectRatio={true} aria-labelledby="{id}-image-label" />
							</div>
							<div class="flex grow flex-col gap-2">
								<div class="flex grow flex-col gap-1">
									<Label for="{id}-value-label" class="font-medium text-black">Header</Label>
									<PlaintextEditor provider={$provider} value={caption} on:input={updateCaption} on:change={handleChange} aria-labelledby="{id}-value-label" />
								</div>
								<div class="flex grow flex-col gap-1">
									<Label for="{id}-value-subheader-label" class="font-medium text-black">Subheader</Label>
									<PlaintextEditor provider={$provider} value={subheader} on:input={updateSubheader} on:change={handleChange} aria-labelledby="{id}-value-subheader-label" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex w-full flex-col gap-2 rounded-lg border p-3">
					<ImageButtonAppearanceEditor {id} {page} {onChange} />
				</div>
				<StateEditor {id} {page} {onChange} />
				{#snippet fallback()}
					<div class="flex flex-col gap-2">
						<EditImage class="h-40 w-40" imgClass="h-auto" bind:error={lastError} value={src} on:change={updateImage} {aspectRatio} initialAspectRatio={IMAGE_BUTTON_DEFAULT.aspectRatio} showAspectRatioPicker={true} preferFreeformAspectRatio={true} />
						<PlaintextEditor provider={$provider} value={caption} on:input={updateCaption} on:change={handleChange} />
					</div>
				{/snippet}
			</FeatureFlagGate>
		</div>
		<div class="h-6 shrink-0" aria-hidden="true"></div>
	</div>
</EditorInset>
